import React, { createContext, useContext, useRef, useState, useEffect } from "react";
import {
  saveCostModelAnalysis,
  fetchLatestCostModelAnalysis,
  fetchLatestSpendCleaning
} from "../services/ExcelDataContext";

// const RESULT_STORAGE_KEY = "spendiq_cost_model_result";

const RAW_EXCEL_STORAGE_KEY = "spendiq_raw_excel_data";
const CLEANED_EXCEL_STORAGE_KEY = "spendiq_cleaned_excel_data";
const SIMPLAI_READY_STORAGE_KEY = "spendiq_simplai_ready_data";

// Backward compatibility with your older code
const EXCEL_STORAGE_KEY = "spendiq_uploaded_excel_data";

type ExcelRow = Record<string, any>;

type ExcelDataContextType = {
  /**
   * Backward-compatible alias.
   * For new flow, excelData points to cleanedExcelData.
   */
  excelData: ExcelRow[];
  setExcelData: (data: ExcelRow[]) => void;

  /**
   * Raw uploaded Excel rows with original headers and uncleaned values.
   */
  rawExcelData: ExcelRow[];
  setRawExcelData: (data: ExcelRow[]) => void;

  /**
   * Cleaned rows with original readable Excel headers.
   * Example:
   * {
   *   "Line Item Description": "Chassis Component",
   *   "Supplier Name": "Magna International"
   * }
   */
  cleanedExcelData: ExcelRow[];
  setCleanedExcelData: (data: ExcelRow[]) => void;

  /**
   * Cleaned rows converted for SimplAI header format.
   * Example:
   * {
   *   "Line_Item_Description": "Chassis Component",
   *   "Supplier_Name": "Magna International"
   * }
   */
  simplAIReadyData: ExcelRow[];
  setSimplAIReadyData: (data: ExcelRow[]) => void;

  analysisResult: Record<string, any> | null;
  setAnalysisResult: (
    data: Record<string, any> | null,
    options?: {
      input_data?: unknown;
      request_id?: string;
      model_name?: string;
      agent_type?: string;
      is_standalone?: boolean;
    }
  ) => void | Promise<void>;
  analysisResultLoading: boolean;

  /** Current on-demand (standalone) cost model result, shared between views without localStorage. */
  standaloneAnalysisResult: Record<string, any> | null;
  setStandaloneAnalysisResult: (data: Record<string, any> | null) => void;

  registerNavigate: (fn: (view: string) => void) => void;
  navigate: (view: string) => void;
};

const ExcelDataContext = createContext<ExcelDataContextType | undefined>(
  undefined
);

const loadArrayFromStorage = (key: string): ExcelRow[] => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const loadObjectFromStorage = (key: string): Record<string, any> | null => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const saveArrayToStorage = (key: string, data: ExcelRow[]) => {
  try {
    if (Array.isArray(data) && data.length > 0) {
      localStorage.setItem(key, JSON.stringify(data));
    } else {
      localStorage.removeItem(key);
    }
  } catch {
    // ignore localStorage errors
  }
};

const saveObjectToStorage = (key: string, data: Record<string, any> | null) => {
  try {
    if (data) {
      localStorage.setItem(key, JSON.stringify(data));
    } else {
      localStorage.removeItem(key);
    }
  } catch {
    // ignore localStorage errors
  }
};



export function ExcelDataProvider({ children }: { children: React.ReactNode }) {
  const [rawExcelData, setRawExcelDataState] = useState<ExcelRow[]>(() =>
    loadArrayFromStorage(RAW_EXCEL_STORAGE_KEY)
  );

  const [cleanedExcelData, setCleanedExcelDataState] = useState<ExcelRow[]>(
    () => {
      const cleaned = loadArrayFromStorage(CLEANED_EXCEL_STORAGE_KEY);

      if (cleaned.length > 0) {
        return cleaned;
      }

      // Backward compatibility fallback
      return loadArrayFromStorage(EXCEL_STORAGE_KEY);
    }
  );

  const [simplAIReadyData, setSimplAIReadyDataState] = useState<ExcelRow[]>(
    () => loadArrayFromStorage(SIMPLAI_READY_STORAGE_KEY)
  );

  

  // const [analysisResult, setAnalysisResultState] =
  //   useState<Record<string, any> | null>(() =>
  //     loadObjectFromStorage(RESULT_STORAGE_KEY)
  //   );
      // Start empty; load latest row from DB in useEffect below
  const [analysisResult, setAnalysisResultState] =
    useState<Record<string, any> | null>(null);

  const [analysisResultLoading, setAnalysisResultLoading] = useState(true);

  const [standaloneAnalysisResult, setStandaloneAnalysisResult] =
    useState<Record<string, any> | null>(null);

  const navigateRef = useRef<((view: string) => void) | null>(null);

    // Load MAIN cost model data = latest row from cost_model_analysis
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const row = await fetchLatestCostModelAnalysis();
        if (cancelled) return;

        if (row?.output_data != null) {
          const output =
            typeof row.output_data === "string"
              ? JSON.parse(row.output_data)
              : row.output_data;
          setAnalysisResultState(output);
        } else {
          setAnalysisResultState(null);
        }
      } catch (e) {
        console.error("Failed to load latest cost model analysis:", e);
        if (!cancelled) setAnalysisResultState(null);
      } finally {
        if (!cancelled) setAnalysisResultLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const setRawExcelData = (data: ExcelRow[]) => {
    setRawExcelDataState(data);
    saveArrayToStorage(RAW_EXCEL_STORAGE_KEY, data);
  };

  const setCleanedExcelData = (data: ExcelRow[]) => {
    setCleanedExcelDataState(data);
    saveArrayToStorage(CLEANED_EXCEL_STORAGE_KEY, data);

    // Backward compatibility: older screens still read excelData
    saveArrayToStorage(EXCEL_STORAGE_KEY, data);
  };

  const setSimplAIReadyData = (data: ExcelRow[]) => {
    setSimplAIReadyDataState(data);
    saveArrayToStorage(SIMPLAI_READY_STORAGE_KEY, data);
  };

  /**
   * Backward-compatible setter.
   * New meaning: setting excelData sets cleanedExcelData.
   */
  const setExcelData = (data: ExcelRow[]) => {
    setCleanedExcelData(data);
  };

  // const setAnalysisResult = (data: Record<string, any> | null) => {
  //   setAnalysisResultState(data);
  //   saveObjectToStorage(RESULT_STORAGE_KEY, data);
  // };

    // Updates UI immediately, then INSERTs a new DB row (does not overwrite old rows)
    // Updates UI immediately, then INSERTs a new DB row (history kept)
  const setAnalysisResult = async (
    data: Record<string, any> | null,
    options?: {
      input_data?: unknown;
      request_id?: string;
      model_name?: string;
      agent_type?: string;
      is_standalone?: boolean;
    }
  ) => {
    setAnalysisResultState(data);

    // Clearing memory only — do not delete DB history
    if (!data) return;

    try {
      let inputPayload = options?.input_data;

      // Prefer explicit input_data if caller passed it.
      // Otherwise load latest cleaned_data from spend cleaning API.
      if (inputPayload === undefined) {
        const cleaningRow = await fetchLatestSpendCleaning();

        // cleaned_data may be object or JSON string depending on driver
        let cleaned = cleaningRow?.cleaned_data ?? null;
        if (typeof cleaned === "string") {
          try {
            cleaned = JSON.parse(cleaned);
          } catch {
            // keep as string if not JSON
          }
        }

        // Your cleaning API stores shape like { rows: [...], headers, model }
        // Prefer rows array when present; otherwise store full cleaned_data
        if (cleaned && typeof cleaned === "object" && Array.isArray((cleaned as any).rows)) {
          inputPayload = (cleaned as any).rows;
        } else {
          inputPayload = cleaned ?? [];
        }
      }

      await saveCostModelAnalysis({
        request_id: options?.request_id || data?.trace_id || undefined,
        input_data: inputPayload ?? [],
        output_data: data, // old spendiq_cost_model_result payload
        model_name: options?.model_name || "cost-model",
        processing_status: "SUCCESS",
        agent_type: options?.agent_type || "cost_model",
        is_standalone: options?.is_standalone ?? false,
      });
    } catch (e) {
      console.error("Failed to persist cost model analysis to DB:", e);
    }
  };

  const registerNavigate = (fn: (view: string) => void) => {
    navigateRef.current = fn;
  };

  const navigate = (view: string) => {
    navigateRef.current?.(view);
  };

  

  return (
    <ExcelDataContext.Provider
      value={{
        // Backward-compatible alias
        excelData: cleanedExcelData,
        setExcelData,

        // New data layers
        rawExcelData,
        setRawExcelData,

        cleanedExcelData,
        setCleanedExcelData,

        simplAIReadyData,
        setSimplAIReadyData,

        analysisResult,
        setAnalysisResult,
        analysisResultLoading,

        standaloneAnalysisResult,
        setStandaloneAnalysisResult,

        registerNavigate,
        navigate,
      }}
    >
      {children}
    </ExcelDataContext.Provider>
  );
}

export function useExcelData() {
  const context = useContext(ExcelDataContext);

  if (!context) {
    throw new Error("useExcelData must be used within ExcelDataProvider");
  }

  return context;
}

export default ExcelDataContext;